import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import Button from '../shared/TailwindComponents/Button';
import Input from '../shared/TailwindComponents/Input';
import Dropdown from '../shared/TailwindComponents/Dropdown';
import Alert from '../shared/TailwindComponents/Alert';
import { FormActions, FormSection } from '../shared/FormComponents';
import OnboardingStepCard, { ONBOARDING_MAX_WIDTH } from './OnboardingStepCard';
import { intakeCountryOptions } from '../Marketplace/marketplaceUi';
import { Account, updateAccountProfile } from '../../services/api/accountService';
import { errorMessage } from '../../services/api/marketplaceService';

/** Norway is the pilot country, so it is the default. */
const DEFAULT_COUNTRY = 'NO';
const NAME_MAX = 100;

interface ClientProfileFormProps {
  account: Account;
  onSaved: (account: Account) => void;
  /** Wording for the first-time step vs. editing later on the account page. */
  mode?: 'setup' | 'edit';
}

/**
 * The client's name and country. The name goes on the engagement letter (and
 * is checked against BankID when they sign); the country decides which
 * lawyers can see their requests.
 */
export const ClientProfileForm: React.FC<ClientProfileFormProps> = ({ account, onSaved, mode = 'setup' }) => {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState(account.profile.first_name || '');
  const [lastName, setLastName] = useState(account.profile.last_name || '');
  const [country, setCountry] = useState(account.profile.country || DEFAULT_COUNTRY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valid = !!firstName.trim() && !!lastName.trim() && !!country
    && firstName.trim().length <= NAME_MAX && lastName.trim().length <= NAME_MAX;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    setSaving(true);
    setError(null);
    try {
      onSaved(await updateAccountProfile({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        country,
      }));
    } catch (err) {
      setError(errorMessage(err, t('Could not save your details. Please try again.')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <OnboardingStepCard
      as="form"
      onSubmit={submit}
      title={mode === 'setup' ? t('What should lawyers call you?') : t('Your details')}
      description={t('Your name is shown to a lawyer only after you accept their proposal.')}
      footer={
        <FormActions>
          <Button type="submit" variant="primary" size="small" disabled={!valid || saving} isLoading={saving}>
            {mode === 'setup' ? t('Continue') : t('Save')}
          </Button>
        </FormActions>
      }
    >
      <FormSection gridCols={2}>
        <Input
          id="client-first-name"
          type="text"
          label={t('First name')}
          value={firstName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
          required
        />
        <Input
          id="client-last-name"
          type="text"
          label={t('Last name')}
          value={lastName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
          required
        />
      </FormSection>
      <Dropdown
        id="client-country"
        label={t('Where is your legal matter?')}
        options={intakeCountryOptions(t)}
        value={country}
        onChange={(v) => setCountry(String(v || ''))}
        fullWidth
      />
      {error && <Alert type="error" message={error} />}
    </OnboardingStepCard>
  );
};

const ClientProfileStep: React.FC<{ account: Account; onSaved: (account: Account) => void }> = (props) => (
  <div className="min-h-screen bg-bg flex items-center justify-center px-4 py-8">
    <div className={clsx('w-full', ONBOARDING_MAX_WIDTH)}>
      <ClientProfileForm {...props} />
    </div>
  </div>
);

export default ClientProfileStep;
